import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { arrearsService, tenantService } from '../../services/api';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import PaymentConfirmationModal from './PaymentConfirmationModal';
import ImportStatementModal from './ImportStatementModal';

const STATUS_STYLES: Record<string, { bg: string; text: string; ring: string; label: string; icon: string }> = {
    ON_TIME:    { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', label: 'ON TIME',    icon: 'fa-check-circle' },
    LATE:       { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',   label: 'LATE',       icon: 'fa-clock' },
    UPCOMING:   { bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-200',    label: 'UPCOMING',   icon: 'fa-bell' },
    OVERDUE:    { bg: 'bg-orange-50',  text: 'text-orange-700',  ring: 'ring-orange-200',  label: 'OVERDUE',    icon: 'fa-exclamation-circle' },
    CRITICAL:   { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-300',    label: 'CRITICAL',   icon: 'fa-times-circle' },
    ENDED:      { bg: 'bg-slate-100',  text: 'text-slate-600',   ring: 'ring-slate-200',   label: 'CLOSED',     icon: 'fa-lock' },
    UNKNOWN:    { bg: 'bg-gray-50',    text: 'text-gray-600',    ring: 'ring-gray-200',    label: 'NO DATA',    icon: 'fa-question-circle' }
};

const statusOf = (status?: string) => STATUS_STYLES[status || 'UNKNOWN'] || STATUS_STYLES.UNKNOWN;

export default function ArrearsDashboard() {
    const navigate = useNavigate();
    const [tenants, setTenants] = useState<any[]>([]);
    const [overview, setOverview] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [modalTenant, setModalTenant] = useState<{ id: string; name: string; rentAmount?: number } | null>(null);
    const [filter, setFilter] = useState<'ALL' | 'AT_RISK' | 'LATE' | 'CRITICAL'>('AT_RISK');
    const [search, setSearch] = useState('');
    // Lazy load — render first 50 tenants, "Load More" reveals next 50.
    // Same pattern as the Properties page so users get a consistent feel.
    const PAGE_SIZE = 50;
    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);

    // Reset to first page whenever the filter or search flips
    useEffect(() => { setPage(1); }, [filter, search]);
    const [showImportModal, setShowImportModal] = useState(false);

    const fetchData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            // Fast path: load dashboard summary first (now single DynamoDB query)
            // Tenant cards render progressively as the per-tenant data arrives.
            const dashPromise = arrearsService.getDashboard().catch(() => ({ data: { data: null } }));
            const tenantsPromise = arrearsService.getTenants().catch(() => ({ data: { data: [] } }));

            // Render dashboard cards as soon as they arrive (before tenant list)
            dashPromise.then((dashRes: any) => {
                setOverview(dashRes?.data?.data || null);
            });

            // Full response once both complete (or one fails)
            const [dashRes, tenantsRes] = await Promise.all([dashPromise, tenantsPromise]);
            setOverview(dashRes?.data?.data || null);
            setTenants(tenantsRes?.data?.data || []);
        } catch (err) {
            console.error('[Arrears] Fetch failed', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Run the notification evaluation on mount so the dashboard reflects
    // 1-week-pre-due and CRITICAL flags even if the cron hasn't fired.
    useEffect(() => {
        arrearsService.evaluateNotifications().catch(() => {});
    }, []);

    const statusFiltered =
        filter === 'AT_RISK' ? tenants.filter(t => ['LATE', 'OVERDUE', 'CRITICAL', 'UPCOMING'].includes(t.status)) :
        filter === 'LATE'     ? tenants.filter(t => t.status === 'LATE') :
        filter === 'CRITICAL' ? tenants.filter(t => t.status === 'CRITICAL') :
        tenants;

    // Search across whichever name/address-ish fields the tenant object
    // actually carries. The arrears API has varied over time (sometimes
    // `propertyAddress`, sometimes `address`, sometimes just `propertyName`)
    // so check several keys defensively rather than crashing on undefined.
    const q = search.trim().toLowerCase();
    const filteredTenants = q
        ? statusFiltered.filter((t: any) => {
            const haystack = [
                t.tenantName, t.tenantEmail,
                t.propertyAddress, t.address, t.propertyName,
                typeof t.address === 'object' ? t.address?.line1 : null,
                typeof t.address === 'object' ? t.address?.postcode : null,
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
        })
        : statusFiltered;

    // Render only the first N pages worth so a 1000-tenant portfolio doesn't
    // try to paint 1000 cards on first load. Load More reveals the next 50.
    const visibleTenants = filteredTenants.slice(0, page * PAGE_SIZE);
    const hasMore = filteredTenants.length > visibleTenants.length;

    const handleLoadMore = () => {
        setLoadingMore(true);
        // setTimeout lets the spinner render before React commits the new
        // batch (keeps the click feeling responsive on slower devices).
        setTimeout(() => {
            setPage(p => p + 1);
            setLoadingMore(false);
        }, 50);
    };

    const totalArrears = overview?.totalArrears ?? 0;
    const inArrearsCount = overview?.inArrearsCount ?? 0;
    const collectionRate = overview?.collectionRate ?? 100;
    const totalTenants = overview?.totalTenants ?? tenants.length;

    // Show spinner only on initial cold load (no data cached yet)
    // Once either overview or tenants arrive, render immediately with a
    // subtle "loading tenants…" indicator instead of a full-page spinner.
    if (loading && !overview && tenants.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                <div className="flex-1 flex justify-center items-center">
                    <div className="animate-pulse flex flex-col items-center">
                        <div className="h-12 w-12 bg-green-200 rounded-full mb-4"></div>
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">

                {/* Header */}
                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            <i className="fas fa-money-bill-wave text-emerald-500 mr-2"></i>
                            Arrears Management
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Monitor rent payments, detect arrears, and take action under the Renters' Rights Act 2025.
                        </p>
                    </div>
                    <div className="mt-4 flex md:mt-0 md:ml-4 gap-2">
                        <button
                            onClick={() => fetchData(false)}
                            disabled={refreshing}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''} mr-2`}></i>
                            Refresh
                        </button>
                        <button
                            onClick={() => setShowImportModal(true)}
                            className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700"
                            title="Drag a CSV from your bank to auto-reconcile payments"
                        >
                            <i className="fas fa-file-csv mr-2"></i>
                            Import Bank Statement
                        </button>
                        <button
                            onClick={() => navigate('/tenants')}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            Manage Tenants
                        </button>
                    </div>
                </div>

                {/* Top Alert Banner for RRA-action cases */}
                {overview && (overview.criticalCount > 0 || overview.overdueCount > 0) && (
                    <div className={`mb-6 rounded-2xl p-4 border-2 ${
                        overview.criticalCount > 0
                            ? 'bg-rose-50 border-rose-300'
                            : 'bg-orange-50 border-orange-300'
                    }`}>
                        <div className="flex items-start gap-3">
                            <i className={`fas ${overview.criticalCount > 0 ? 'fa-gavel' : 'fa-exclamation-triangle'} text-2xl ${
                                overview.criticalCount > 0 ? 'text-rose-600' : 'text-orange-600'
                            }`}></i>
                            <div className="flex-1">
                                <h3 className="text-sm font-black uppercase tracking-tight text-gray-900">
                                    {overview.criticalCount > 0
                                        ? `${overview.criticalCount} tenant(s) in CRITICAL arrears — RRA action recommended`
                                        : `${overview.overdueCount} tenant(s) past 1 week — formal notice required`}
                                </h3>
                                <p className="text-xs text-gray-700 mt-1">
                                    {overview.criticalCount > 0
                                        ? 'Under the Renters\' Rights Act 2025 (Ground 8), 8+ weeks of unpaid rent allows mandatory possession. Click any CRITICAL card below to record a payment, send a Section 8 notice, or start eviction proceedings.'
                                        : 'Issue a formal arrears notice as the first step before any possession action. Open a tenant card below to take action.'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Dashboard Cards — real data only */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-4 mb-8">
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Total Arrears</dt>
                            <dd className={`mt-1 text-3xl font-semibold ${totalArrears > 0 ? 'text-rose-600' : 'text-gray-900'}`}>
                                £{Number(totalArrears).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </dd>
                            <p className="text-[10px] text-gray-400 mt-1">across {inArrearsCount} tenant(s)</p>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Tenants in Arrears</dt>
                            <dd className={`mt-1 text-3xl font-semibold ${inArrearsCount > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                {inArrearsCount} <span className="text-base text-gray-400">/ {totalTenants}</span>
                            </dd>
                            {overview && (overview.criticalCount > 0 || (overview.lateOrOverdueCount ?? overview.overdueCount) > 0) && (
                                <p className="text-[10px] text-gray-500 mt-1">
                                    {overview.criticalCount} critical · {overview.lateOrOverdueCount ?? overview.overdueCount} overdue
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Collection Rate</dt>
                            <dd className={`mt-1 text-3xl font-semibold ${
                                collectionRate >= 95 ? 'text-emerald-600' :
                                collectionRate >= 80 ? 'text-amber-600' : 'text-rose-600'
                            }`}>
                                {collectionRate}%
                            </dd>
                            <p className="text-[10px] text-gray-400 mt-1">
                                £{Number(overview?.totalPaid || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })} of £{Number(overview?.totalExpected || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                            </p>
                        </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                            <dt className="text-sm font-medium text-gray-500 truncate">Upcoming (7 days)</dt>
                            <dd className={`mt-1 text-3xl font-semibold ${(overview?.upcomingCount || 0) > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                                {overview?.upcomingCount || 0}
                            </dd>
                            <p className="text-[10px] text-gray-400 mt-1">pre-due reminders scheduled</p>
                        </div>
                    </div>
                </div>

                {/* Filter Tabs + Search */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <button
                        onClick={() => setFilter('AT_RISK')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-tighter transition ${
                            filter === 'AT_RISK'
                                ? 'bg-rose-600 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <i className="fas fa-exclamation-triangle mr-1.5"></i>
                        Action Required ({tenants.filter(t => ['LATE', 'OVERDUE', 'CRITICAL', 'UPCOMING'].includes(t.status)).length})
                    </button>
                    <button
                        onClick={() => setFilter('LATE')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-tighter transition ${
                            filter === 'LATE'
                                ? 'bg-amber-500 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <i className="fas fa-clock mr-1.5"></i>
                        Late ({tenants.filter(t => t.status === 'LATE').length})
                    </button>
                    <button
                        onClick={() => setFilter('CRITICAL')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-tighter transition ${
                            filter === 'CRITICAL'
                                ? 'bg-rose-700 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <i className="fas fa-times-circle mr-1.5"></i>
                        Critical ({tenants.filter(t => t.status === 'CRITICAL').length})
                    </button>
                    <button
                        onClick={() => setFilter('ALL')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-tighter transition ${
                            filter === 'ALL'
                                ? 'bg-slate-900 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <i className="fas fa-users mr-1.5"></i>
                        All Tenants ({tenants.length})
                    </button>
                    <div className="relative flex-1 min-w-[200px] max-w-xs ml-auto">
                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400"></i>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name or address..."
                            className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-100"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                aria-label="Clear search"
                            >
                                <i className="fas fa-times text-[10px]"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* Tenants Grid */}
                {loading && overview && (
                    <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
                        <i className="fas fa-spinner fa-spin"></i>
                        Loading tenant details…
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {visibleTenants.map((tenant: any) => {
                        const s = statusOf(tenant.status);
                        const rec = tenant.recommendation;
                        return (
                            <div
                                key={tenant.tenantId}
                                className={`bg-white rounded-xl border-2 p-4 shadow-sm hover:shadow-md transition-all group ${
                                    tenant.status === 'CRITICAL' ? 'border-rose-300 ring-2 ring-rose-100' :
                                    tenant.status === 'OVERDUE' ? 'border-orange-300' :
                                    tenant.status === 'LATE' ? 'border-amber-300' :
                                    tenant.status === 'UPCOMING' ? 'border-blue-200' :
                                    'border-slate-200'
                                }`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                            tenant.status === 'CRITICAL' ? 'bg-rose-100 text-rose-500' :
                                            tenant.status === 'OVERDUE' ? 'bg-orange-100 text-orange-500' :
                                            tenant.status === 'LATE' ? 'bg-amber-100 text-amber-500' :
                                            tenant.status === 'UPCOMING' ? 'bg-blue-100 text-blue-500' :
                                            'bg-emerald-50 text-emerald-500'
                                        }`}>
                                            <i className="fas fa-user-circle text-xl"></i>
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="text-sm font-black text-slate-900 truncate uppercase tracking-tight">{tenant.tenantName}</h4>
                                            <p className="text-[10px] font-bold text-slate-400 truncate -mt-0.5">{tenant.tenantEmail || '—'}</p>
                                        </div>
                                    </div>
                                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap uppercase ${s.bg} ${s.text}`}>
                                        <i className={`fas ${s.icon} mr-1`}></i>
                                        {s.label}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-2 py-3 border-y border-slate-50 mb-3">
                                    <div>
                                        <p className="text-[9px] font-black text-slate-300 uppercase leading-none mb-1">Rent Amount</p>
                                        <p className="text-xs font-black text-slate-900 leading-none">£{Number(tenant.rentAmount || 0).toFixed(2)}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[9px] font-black text-slate-300 uppercase leading-none mb-1">Arrears</p>
                                        <p className={`text-xs font-black leading-none ${(tenant.arrears || 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                            £{Number(tenant.arrears || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                    </div>
                                </div>

                                {/* Inline recommendation if any */}
                                {rec && rec.severity !== 'INFO' && (
                                    <div className={`mb-3 p-2 rounded-lg text-[10px] font-medium ${
                                        rec.severity === 'CRITICAL' ? 'bg-rose-50 text-rose-800' :
                                        rec.severity === 'HIGH' ? 'bg-orange-50 text-orange-800' :
                                        'bg-amber-50 text-amber-800'
                                    }`}>
                                        <i className={`fas ${
                                            rec.severity === 'CRITICAL' ? 'fa-gavel' :
                                            rec.severity === 'HIGH' ? 'fa-exclamation-triangle' :
                                            'fa-bell'
                                        } mr-1.5`}></i>
                                        {rec.title}
                                    </div>
                                )}

                                <div className="flex items-center gap-2">
                                    <button
                                        className="flex-1 bg-slate-900 text-white text-[10px] font-black px-3 py-2 rounded-lg hover:bg-emerald-600 transition-all uppercase tracking-tighter shadow-sm"
                                        onClick={() => navigate('/communications', { state: { tenantId: tenant.tenantId } })}
                                    >
                                        <i className="fas fa-paper-plane mr-2 opacity-50"></i>
                                        Contact
                                    </button>
                                    <button
                                        onClick={() => setModalTenant({ id: tenant.tenantId, name: tenant.tenantName, rentAmount: tenant.rentAmount })}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all"
                                        title="Record payment / view rent schedule"
                                    >
                                        <i className="fas fa-ellipsis-h text-xs"></i>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    {visibleTenants.length === 0 && (
                        <div className="col-span-full py-24 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                            <div className="w-16 h-16 bg-emerald-50 text-emerald-300 rounded-full flex items-center justify-center text-2xl mx-auto mb-4 border border-emerald-100">
                                <i className="fas fa-check-double"></i>
                            </div>
                            <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">
                                {filter === 'AT_RISK' ? 'No tenants in arrears' :
                                 filter === 'LATE' ? 'No late tenants' :
                                 filter === 'CRITICAL' ? 'No tenants in critical arrears' :
                                 'No tenants yet'}
                            </h4>
                            <p className="text-xs text-slate-400 font-medium mt-1">
                                {filter === 'AT_RISK' ? 'All tenants are up to date with rent. Tenants with upcoming, late, or overdue payments will appear here.' :
                                 filter === 'LATE' ? 'No tenants currently have a LATE rent status.' :
                                 filter === 'CRITICAL' ? 'No tenants currently have a CRITICAL rent status.' :
                                 'Add tenants to begin monitoring rent performance.'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Load More — same pattern as Properties page. Renders 50 cards
                    at a time so a 1000-tenant portfolio doesn't try to paint
                    everything on first load. */}
                {hasMore && (
                    <div className="text-center mt-6">
                        <button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            className="px-6 py-3 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-xl shadow-sm hover:bg-slate-50 hover:border-emerald-300 disabled:opacity-50 transition-all"
                        >
                            {loadingMore
                                ? (<><i className="fas fa-spinner fa-spin mr-2"></i>Loading…</>)
                                : (<>Load More ({Math.min(filteredTenants.length - visibleTenants.length, PAGE_SIZE)} more · {filteredTenants.length - visibleTenants.length} remaining)</>)}
                        </button>
                        <p className="text-[10px] text-slate-400 mt-2">
                            Showing {visibleTenants.length} of {filteredTenants.length} tenants
                        </p>
                    </div>
                )}
            </div>
            <Footer />

            {modalTenant && (
                <PaymentConfirmationModal
                    isOpen={!!modalTenant}
                    onClose={() => setModalTenant(null)}
                    tenantId={modalTenant.id}
                    tenantName={modalTenant.name}
                    rentAmount={modalTenant.rentAmount}
                    onPaymentRecorded={() => fetchData(true)}
                    onSendMessage={(templateId) => {
                        setModalTenant(null);
                        navigate('/communications', { state: { tenantId: modalTenant.id, templateId } });
                    }}
                />
            )}

            <ImportStatementModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onComplete={() => fetchData(true)}
            />
        </div>
    );
}
